/**
 * Service for the cms.dispatchers data-management editor.
 *
 * Y/N CHAR(1) columns are converted to booleans on the wire by the backend
 * (see backend/app/api/api_v1/endpoints/dispatchers.py). The frontend works
 * exclusively with booleans.
 */
import api from './api';

export interface Dispatcher {
  recno: number;
  username: string;
  displayname: string;
  gwi: boolean;
  region: string;
  preference: string;
  defaultdriver: string;
  revenueregion: string;
  dispatcheremail: string;
  doemail: string;
  radio: string;
  autoprint: boolean;
  autopregate: boolean;
  deleted: boolean;
  is_driver_dispatcher: boolean;
  is_order_dispatcher: boolean;
  /** Smart Preplan plans this dispatcher's drivers; off takes the whole fleet out. */
  preplan_fleet: boolean;
  azure_user_id: string;
}

export type DispatcherUpdate = Partial<Omit<Dispatcher, 'recno'>>;
// New dispatcher — username required, everything else optional (defaults off/empty).
export type DispatcherCreate = Partial<Omit<Dispatcher, 'recno' | 'username'>> & { username: string };

interface ListResponse {
  data: Dispatcher[];
  count: number;
}

interface SingleResponse {
  data: Dispatcher;
  message?: string;
}

export interface ListFilters {
  includeDeleted?: boolean;
  isDriverDispatcher?: boolean;
  includeDriverRoster?: boolean;
  isOrderDispatcher?: boolean;
}

const dispatchersService = {
  list: async (filters: ListFilters = {}, signal?: AbortSignal): Promise<Dispatcher[]> => {
    const params: Record<string, string | boolean> = {};
    if (filters.includeDriverRoster) params.include_driver_roster = true;
    if (filters.includeDeleted) params.include_deleted = true;
    if (filters.isDriverDispatcher !== undefined) params.is_driver_dispatcher = filters.isDriverDispatcher;
    if (filters.isOrderDispatcher !== undefined) params.is_order_dispatcher = filters.isOrderDispatcher;
    const res = await api.get<ListResponse>('/dispatchers', { params, signal });
    return res.data.data;
  },

  get: async (recno: number): Promise<Dispatcher> => {
    const res = await api.get<SingleResponse>(`/dispatchers/${recno}`);
    return res.data.data;
  },

  update: async (recno: number, payload: DispatcherUpdate): Promise<Dispatcher> => {
    const res = await api.put<SingleResponse>(`/dispatchers/${recno}`, payload);
    return res.data.data;
  },

  create: async (payload: DispatcherCreate): Promise<Dispatcher> => {
    const res = await api.post<SingleResponse>('/dispatchers', payload);
    return res.data.data;
  },
};

export default dispatchersService;
